import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tenantSettingsSchema, type TenantSettings } from '@dairy/shared';
import { useIsOwner, useSettings } from '@/auth/useSession';
import { Button, Card, Field, Input, PageHeader } from '@/components/ui';
import { useSaveTenant } from './useTenantSettings';

type Key = keyof TenantSettings;

/** S3-T12: breeding and alert parameters (owner) with the allowed ranges. */
const FIELDS: Array<{ key: Key; min: number; max: number; step?: number }> = [
  { key: 'gestationDays', min: 270, max: 295 },
  { key: 'dryPeriodDays', min: 30, max: 90 },
  { key: 'heatWatchStartDays', min: 15, max: 30 },
  { key: 'heatWatchEndDays', min: 15, max: 30 },
  { key: 'pregCheckDays', min: 30, max: 120 },
  { key: 'breedingStartDays', min: 30, max: 120 },
  { key: 'heiferBreedingAgeMonths', min: 12, max: 30 },
  { key: 'calfMaxAgeMonths', min: 6, max: 18 },
  { key: 'colostrumDays', min: 2, max: 7 },
  { key: 'milkDropThreshold', min: 0.5, max: 0.95, step: 0.05 },
  { key: 'lateEntryHours', min: 1, max: 24 },
  { key: 'managerEditWindowHours', min: 1, max: 168 },
  { key: 'receivableOverdueDays', min: 1, max: 365 },
  { key: 'unaccountedMilkThreshold', min: 0, max: 0.2, step: 0.01 },
];

export function BreedingSettingsPage() {
  const { t } = useTranslation();
  const s = useSettings();
  const isOwner = useIsOwner();
  const { saveTenant, saving } = useSaveTenant();
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(FIELDS.map((f) => [f.key, String(s[f.key])])));
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    const next = { ...s, ...Object.fromEntries(FIELDS.map((f) => [f.key, Number(values[f.key])])) };
    const r = tenantSettingsSchema.safeParse(next);
    if (!r.success) return setError(t('settings.rangeError', { field: t(`settings.param.${String(r.error.issues[0]?.path[0])}`) }));
    setError(null);
    await saveTenant({ settings: r.data });
  };
  return (
    <div className="mx-auto max-w-lg space-y-4">
      <PageHeader title={t('settings.breeding')} />
      <Card className="grid gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => (
          <Field key={f.key} label={t(`settings.param.${f.key}`)} hint={`${f.min} – ${f.max}`}>
            <Input
              type="number"
              inputMode="decimal"
              min={f.min}
              max={f.max}
              step={f.step ?? 1}
              value={values[f.key]}
              disabled={!isOwner}
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
            />
          </Field>
        ))}
      </Card>
      {error ? <p className="text-red-700">{error}</p> : null}
      {isOwner ? (
        <Button className="w-full" disabled={saving} onClick={() => void submit()}>
          {t('common.save')}
        </Button>
      ) : null}
    </div>
  );
}
